import React, { useState, useEffect } from 'react';
import { 
  Wifi, Clock, Thermometer, Sun, Activity, 
  Navigation, Settings, ChevronDown, ChevronUp,
  Check, X, AlertCircle
} from 'lucide-react';
import { Device, supabase } from '../lib/supabase';

interface DeviceConfigPanelProps {
  device: Device;
  onSave?: (updates: Partial<Device>) => Promise<void>;
  readOnly?: boolean;
}

interface DeviceConfig extends Device {
  apn?: string;
  main_server?: string;
  terminal_phone?: string;
  report_interval_idle?: number;
  report_interval_movement?: number;
  movement_speed_threshold?: number;
  park_timer?: number;
  temperature_enabled?: boolean;
  temperature_min?: number;
  temperature_max?: number;
  humidity_min?: number;
  humidity_max?: number;
  light_enabled?: boolean;
  light_threshold?: number;
  g_sensor_enabled?: boolean;
  gnss_enabled?: boolean;
  additional_config?: any;
  raw_config?: any;
}

export default function DeviceConfigPanel({ device, onSave, readOnly = false }: DeviceConfigPanelProps) {
  const [isExpanded, setIsExpanded] = useState({
    network: true,
    reporting: true,
    sensors: true,
    advanced: false
  });

  const [isEditing, setIsEditing] = useState(false);
  const [deviceWithConfig, setDeviceWithConfig] = useState<DeviceConfig>(device);
  const [editedConfig, setEditedConfig] = useState<DeviceConfig>(device);
  const [loading, setLoading] = useState(false); // Start with false to show immediate data
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadDeviceConfig = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch configuration data
        const { data: configData, error: fetchError } = await supabase
          .from('device_config')
          .select('*')
          .eq('device_id', device.device_id)
          .single();

        if (fetchError) {
          setError(`Failed to load configuration: ${fetchError.message}`);
          console.error('Error fetching config:', fetchError);
        } else if (configData) {
          const mergedDevice: DeviceConfig = {
            ...device,
            apn: configData.apn,
            main_server: configData.main_server,
            terminal_phone: configData.terminal_phone,
            report_interval_idle: configData.report_interval_idle,
            report_interval_movement: configData.report_interval_movement,
            movement_speed_threshold: configData.movement_speed_threshold,
            park_timer: configData.park_timer,
            temperature_enabled: configData.temperature_enabled,
            temperature_min: configData.temperature_min,
            temperature_max: configData.temperature_max,
            humidity_min: configData.humidity_min,
            humidity_max: configData.humidity_max,
            light_enabled: configData.light_enabled,
            light_threshold: configData.light_threshold,
            g_sensor_enabled: configData.g_sensor_enabled,
            gnss_enabled: configData.gnss_enabled,
            additional_config: configData.additional_config,
            raw_config: configData.raw_config
          };
          setDeviceWithConfig(mergedDevice);
          setEditedConfig(mergedDevice);
        }
      } catch (error) {
        console.error('Error loading device config:', error);
        setError('Failed to load configuration');
      } finally {
        setLoading(false);
      }
    };

    // Only load if we have a device_id
    if (device.device_id) {
      loadDeviceConfig();
    } else {
      setLoading(false);
    }
  }, [device]);

  const handleSave = async () => {
    if (onSave) {
      await onSave(editedConfig);
      setIsEditing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-gray-500">Loading configuration...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const SectionHeader = ({ 
    title, 
    icon: Icon, 
    section 
  }: { 
    title: string; 
    icon: React.ElementType; 
    section: keyof typeof isExpanded 
  }) => (
    <div 
      className="flex items-center justify-between p-4 bg-gray-50 rounded-t-lg cursor-pointer hover:bg-gray-100 transition-colors"
      onClick={() => setIsExpanded(prev => ({ ...prev, [section]: !prev[section] }))}
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      {isExpanded[section] ? 
        <ChevronUp className="w-5 h-5 text-gray-400" /> : 
        <ChevronDown className="w-5 h-5 text-gray-400" />
      }
    </div>
  );

  const ConfigField = ({ 
    label, 
    value, 
    unit, 
    field,
    type = 'text'
  }: { 
    label: string; 
    value: any; 
    unit?: string;
    field: keyof Device;
    type?: string;
  }) => (
    <div className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600">{label}:</span>
      <div className="flex items-center gap-2">
        {isEditing && !readOnly ? (
          <input
            type={type}
            value={String(editedConfig[field] || '')}
            onChange={(e) => setEditedConfig(prev => ({
              ...prev,
              [field]: type === 'number' ? Number(e.target.value) : e.target.value
            }))}
            className="px-2 py-1 border rounded text-sm flex-1"
          />
        ) : (
          <span className="text-sm font-medium">
            {value !== undefined && value !== null ? String(value) : 'Not configured'}
          </span>
        )}
        {unit && <span className="text-xs text-gray-500">{unit}</span>}
      </div>
    </div>
  );

  const SensorStatus = ({ 
    label, 
    enabled, 
    icon: Icon,
    details 
  }: { 
    label: string; 
    enabled?: boolean; 
    icon: React.ElementType;
    details?: string;
  }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-gray-600" />
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        {details && <span className="text-xs text-gray-500">{details}</span>}
        <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
          enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
        }`}>
          {enabled ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
          {enabled ? 'ON' : 'OFF'}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Device Configuration</h2>
          <p className="text-sm text-gray-600 mt-1">
            {deviceWithConfig.device_nickname} ({deviceWithConfig.device_id})
          </p>
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditedConfig(deviceWithConfig);
                    setIsEditing(false);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
              >
                Edit Configuration
              </button>
            )}
          </div>
        )}
      </div>

      {/* Network Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SectionHeader title="Network Configuration" icon={Wifi} section="network" />
        {isExpanded.network && (
          <div className="p-4 space-y-2">
            <ConfigField 
              label="APN" 
              value={deviceWithConfig.apn || ''} 
              field="apn"
            />
            <ConfigField 
              label="Main Server" 
              value={deviceWithConfig.main_server || ''} 
              field="main_server"
            />
            <ConfigField 
              label="Terminal Phone" 
              value={deviceWithConfig.terminal_phone || ''} 
              field="terminal_phone"
            />
          </div>
        )}
      </div>

      {/* Reporting Intervals */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SectionHeader title="Reporting Intervals" icon={Clock} section="reporting" />
        {isExpanded.reporting && (
          <div className="p-4 space-y-2">
            <ConfigField 
              label="Idle Interval" 
              value={deviceWithConfig.report_interval_idle} 
              unit="seconds"
              field="report_interval_idle"
              type="number"
            />
            <ConfigField 
              label="Movement Interval" 
              value={deviceWithConfig.report_interval_movement} 
              unit="meters"
              field="report_interval_movement"
              type="number"
            />
            <ConfigField 
              label="Movement Speed Threshold" 
              value={deviceWithConfig.movement_speed_threshold} 
              unit="km/h"
              field="movement_speed_threshold"
              type="number"
            />
            <ConfigField 
              label="Park Timer" 
              value={deviceWithConfig.park_timer} 
              unit="seconds"
              field="park_timer"
              type="number"
            />
          </div>
        )}
      </div>

      {/* Sensor Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SectionHeader title="Sensor Configuration" icon={Activity} section="sensors" />
        {isExpanded.sensors && (
          <div className="p-4 space-y-3">
            <SensorStatus 
              label="Temperature & Humidity" 
              enabled={deviceWithConfig.temperature_enabled}
              icon={Thermometer}
              details={deviceWithConfig.temperature_enabled ? 
                `${deviceWithConfig.temperature_min}°C - ${deviceWithConfig.temperature_max}°C / ${deviceWithConfig.humidity_min}% - ${deviceWithConfig.humidity_max}%` : 
                undefined
              }
            />
            <SensorStatus 
              label="Light Sensor" 
              enabled={deviceWithConfig.light_enabled}
              icon={Sun}
              details={deviceWithConfig.light_enabled ? 
                `Threshold: ${deviceWithConfig.light_threshold}` : 
                undefined
              }
            />
            <SensorStatus 
              label="G-Sensor (Motion)" 
              enabled={deviceWithConfig.g_sensor_enabled}
              icon={Activity}
            />
            <SensorStatus 
              label="GNSS (GPS)" 
              enabled={deviceWithConfig.gnss_enabled}
              icon={Navigation}
            />
            
            {/* Threshold Settings */}
            {(deviceWithConfig.temperature_enabled || deviceWithConfig.light_enabled) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Threshold Settings</h4>
                {deviceWithConfig.temperature_enabled && (
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="text-xs text-gray-600">Temperature Range</label>
                      <div className="text-sm font-medium">
                        {deviceWithConfig.temperature_min}°C - {deviceWithConfig.temperature_max}°C
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Humidity Range</label>
                      <div className="text-sm font-medium">
                        {deviceWithConfig.humidity_min}% - {deviceWithConfig.humidity_max}%
                      </div>
                    </div>
                  </div>
                )}
                {deviceWithConfig.light_enabled && (
                  <div>
                    <label className="text-xs text-gray-600">Light Threshold</label>
                    <div className="text-sm font-medium">{deviceWithConfig.light_threshold} lux</div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Advanced Configuration */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <SectionHeader title="Advanced Configuration" icon={Settings} section="advanced" />
        {isExpanded.advanced && (
          <div className="p-4">
            {/* Debug info */}
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-800 mb-2">Debug Info</h4>
              <p className="text-xs text-blue-600">Device ID: {device.device_id}</p>
              <p className="text-xs text-blue-600">Loading: {loading ? 'Yes' : 'No'}</p>
              <p className="text-xs text-blue-600">Error: {error || 'None'}</p>
              <p className="text-xs text-blue-600">Has Config Data: {deviceWithConfig.apn ? 'Yes' : 'No'}</p>
              <div className="mt-2">
                <p className="text-xs font-medium text-blue-700">Current Values:</p>
                <pre className="text-xs text-blue-600 mt-1">
{JSON.stringify({
  apn: deviceWithConfig.apn,
  main_server: deviceWithConfig.main_server,
  terminal_phone: deviceWithConfig.terminal_phone,
  report_interval_idle: deviceWithConfig.report_interval_idle
}, null, 2)}
                </pre>
              </div>
            </div>
            
            {deviceWithConfig.additional_config && (
              <div className="bg-gray-50 rounded-lg p-4">
                <pre className="text-xs text-gray-700 overflow-auto">
                  {JSON.stringify(deviceWithConfig.additional_config, null, 2)}
                </pre>
              </div>
            )}
            {deviceWithConfig.raw_config && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-yellow-600" />
                  <span className="text-sm font-medium text-gray-700">Raw Device Configuration</span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <pre className="text-xs text-gray-600 overflow-auto max-h-64">
                    {JSON.stringify(deviceWithConfig.raw_config, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}